import React, { useState, useEffect, useRef } from 'react';
import api, { companyAPI } from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { useConfirm } from '../components/ConfirmModal';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTHS_S = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const INR = n => '₹' + (n||0).toLocaleString('en-IN', { maximumFractionDigits:0 });

const INP = { width:'100%', padding:'10px 14px', border:'1.5px solid #e2e8f0', borderRadius:10, fontSize:13, fontFamily:"'Plus Jakarta Sans',sans-serif", outline:'none', boxSizing:'border-box', background:'#fafbfc', transition:'border .2s' };

export default function Salary() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const confirm = useConfirm();
  const printRef = useRef();

  const [company,      setCompany]      = useState({});
  const [tab,          setTab]          = useState('payslips');
  const [payslips,     setPayslips]     = useState([]);
  const [allPayslips,  setAllPayslips]  = useState([]);
  const [employees,    setEmployees]    = useState([]);
  const [structures,   setStructures]   = useState({});  // empId → structure
  const [selected,     setSelected]     = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [genForm,      setGenForm]      = useState({ employeeId:'', month: new Date().getMonth()+1, year: new Date().getFullYear() });
  const [bulkForm,     setBulkForm]     = useState({ month: new Date().getMonth()+1, year: new Date().getFullYear() });
  const [structForm,   setStructForm]   = useState({ employeeId:'', basicSalary:'', hra:'', transport:'', medical:'', otherAllowance:'', pfPercent:12, esiPercent:0.75 });
  const [filterMonth,  setFilterMonth]  = useState('');
  const [filterYear,   setFilterYear]   = useState(new Date().getFullYear());
  const [filterStatus, setFilterStatus] = useState('');
  const [structLoaded, setStructLoaded] = useState(false);

  const [salaryVisible, setSalaryVisible] = useState(true); // assume visible until company loaded

  useEffect(() => {
    companyAPI.get().then(r => {
      const cs = r.data.companySettings || {};
      setCompany(cs);
      if (!isAdmin) setSalaryVisible(cs.showSalaryToEmployees === true);
    }).catch(()=>{});
    if (isAdmin) { loadAll(); loadEmployees(); }
    if (isAdmin || true) loadPayslips(); // always attempt; backend will gate
  }, []);

  const loadPayslips = async () => {
    setLoading(true);
    try { const r = await api.get('/salary/my'); setPayslips(r.data.data||[]); } catch(err) {
      if (err?.response?.status === 403) setSalaryVisible(false);
    }
    setLoading(false);
  };

  const loadAll = async () => {
    try { const r = await api.get('/salary/all'); setAllPayslips(r.data.data||[]); } catch {}
  };

  const loadEmployees = async () => {
    try { const r = await api.get('/users'); setEmployees((r.data.users||[]).filter(e=>e.role==='team'||e.role==='manager')); } catch {}
  };

  const loadStructure = async (empId) => {
    if (!empId || structures[empId] !== undefined) return;
    try {
      const r = await api.get(`/salary/structure/${empId}`);
      const s = r.data.data;
      setStructures(prev => ({ ...prev, [empId]: s }));
      setStructForm({ employeeId:empId, basicSalary:s.basicSalary||'', hra:s.hra||'', transport:s.transport||'', medical:s.medical||'', otherAllowance:s.otherAllowance||'', pfPercent:s.pfPercent||12, esiPercent:s.esiPercent||0.75 });
    } catch {
      setStructures(prev => ({ ...prev, [empId]: null }));
      setStructForm(f => ({ ...f, employeeId:empId }));
    }
  };

  const generate = async (e) => {
    e.preventDefault();
    try { const r = await api.post('/salary/generate', genForm); toast.success('Payslip generated!'); loadAll(); setSelected(r.data.data); setTab('all'); }
    catch(e) { toast.error(e.response?.data?.message||'Failed'); }
  };

  const generateBulk = async () => {
    const ok = await confirm(`Generate payslips for ALL employees for ${MONTHS_S[bulkForm.month-1]} ${bulkForm.year}?`, { title: 'Generate Payslips?', variant: 'warning' });
    if (!ok) return;
    try {
      const r = await api.post('/salary/generate-bulk', bulkForm);
      toast.success(r.data.message); loadAll();
    } catch { toast.error('Failed'); }
  };

  const markPaid = async (id, method='bank') => {
    try { await api.patch(`/salary/payslip/${id}/mark-paid`, { paymentMethod:method }); toast.success('Marked as paid!'); loadAll(); if(selected?._id===id) setSelected(p=>({...p,status:'paid'})); }
    catch { toast.error('Failed'); }
  };

  const deletePayslip = async (id) => {
    const ok = await confirm('Delete this payslip? This cannot be undone.', { title: 'Delete Payslip?', variant: 'danger' });
    if (!ok) return;
    try { await api.delete(`/salary/payslip/${id}`); toast.success('Payslip deleted'); loadAll(); if (selected?._id === id) setSelected(null); }
    catch { toast.error('Failed to delete'); }
  };

  const saveStructure = async (e) => {
    e.preventDefault();
    try { await api.post('/salary/structure', structForm); toast.success('Salary structure saved!'); setStructures(prev=>({...prev,[structForm.employeeId]:structForm})); }
    catch(e) { toast.error(e.response?.data?.message||'Failed'); }
  };

  const handlePrint = () => {
    if (!selected) return;
    const co = company;
    const logoHtml = co.logoBase64
      ? `<img src="${co.logoBase64}" alt="logo" style="max-height:70px;max-width:200px;object-fit:contain;"/>`
      : `<div style="font-size:28px;font-weight:900;color:#1e293b">${co.name || 'Your Company'}</div>`;

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Payslip - ${selected.employee?.name || ''}</title>
<style>
* { box-sizing:border-box; margin:0; padding:0; }
body { font-family: 'Segoe UI', Arial, sans-serif; font-size:13px; color:#1e293b; padding:40px; background:#fff; }
.ps-header { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:3px solid #1e293b; padding-bottom:20px; margin-bottom:24px; }
.co-name { font-size:22px; font-weight:900; color:#1e293b; margin-bottom:3px; }
.co-tagline { font-size:12px; color:#6366f1; margin-bottom:4px; }
.co-meta { font-size:11px; color:#64748b; line-height:1.6; }
.ps-badge { background:#1e293b; color:#fff; padding:6px 16px; border-radius:8px; font-size:12px; font-weight:800; display:inline-block; margin-bottom:6px; }
.ps-meta { display:grid; grid-template-columns:repeat(4,1fr); gap:14px; background:#f8fafc; border-radius:12px; padding:18px; margin-bottom:22px; }
.ps-meta .ml { font-size:10px; text-transform:uppercase; letter-spacing:.06em; color:#94a3b8; font-weight:700; }
.ps-meta .mv { font-size:14px; font-weight:700; color:#1e293b; margin-top:2px; }
.ps-cols { display:grid; grid-template-columns:1fr 1fr; gap:20px; margin-bottom:18px; }
.ps-sec h4 { font-size:11px; text-transform:uppercase; letter-spacing:.06em; color:#94a3b8; font-weight:700; margin-bottom:10px; }
.ps-row { display:flex; justify-content:space-between; padding:7px 0; border-bottom:1px solid #f1f5f9; font-size:12px; color:#475569; }
.ps-row span:last-child { font-weight:600; color:#0f172a; }
.ps-row.red span:last-child { color:#ef4444; }
.ps-total { display:flex; justify-content:space-between; padding:10px 0; font-weight:800; font-size:14px; border-top:2px solid #1e293b; margin-top:4px; }
.ps-net { background:#1e293b; color:#fff; border-radius:12px; padding:20px 24px; display:flex; justify-content:space-between; align-items:center; }
.ps-net .amount { font-size:32px; font-weight:900; }
.ps-net .sub { font-size:12px; opacity:.7; margin-top:4px; }
.bank-box { background:#f8fafc; border-radius:10px; padding:16px; margin-top:18px; font-size:12px; }
.bank-box h4 { font-size:11px; text-transform:uppercase; letter-spacing:.06em; color:#94a3b8; font-weight:700; margin-bottom:8px; }
.bank-grid { display:grid; grid-template-columns:1fr 1fr 1fr; gap:10px; }
.bank-item label { display:block; font-size:10px; color:#94a3b8; font-weight:700; margin-bottom:2px; }
.bank-item div { font-weight:600; color:#1e293b; }
.footer { margin-top:28px; padding-top:14px; border-top:1px solid #e2e8f0; display:flex; justify-content:space-between; font-size:11px; color:#94a3b8; }
@media print { body { padding:20px; } }
</style></head><body>

<div class="ps-header">
  <div>
    ${logoHtml}
    ${co.name ? `<div class="co-name" style="margin-top:6px">${co.name}</div>` : ''}
    ${co.tagline ? `<div class="co-tagline">${co.tagline}</div>` : ''}
    <div class="co-meta">
      ${co.address ? co.address + '<br/>' : ''}
      ${[co.city, co.country].filter(Boolean).join(', ')}${co.phone ? ' · ' + co.phone : ''}<br/>
      ${co.email || ''}${co.website ? ' · ' + co.website : ''}
      ${co.gstNumber ? '<br/>GST: ' + co.gstNumber : ''}
    </div>
  </div>
  <div style="text-align:right">
    <div class="ps-badge">PAYSLIP</div>
    <div style="font-size:13px;color:#64748b;margin-top:4px">${MONTHS[selected.month-1]} ${selected.year}</div>
    <div style="font-size:12px;font-weight:700;color:${selected.status==='paid'?'#22c55e':'#f59e0b'};margin-top:4px">${selected.status.toUpperCase()}</div>
  </div>
</div>

<div class="ps-meta">
  <div><div class="ml">Employee</div><div class="mv">${selected.employee?.name || user?.name || '—'}</div></div>
  <div><div class="ml">Position</div><div class="mv">${selected.employee?.position || user?.position || '—'}</div></div>
  <div><div class="ml">Working Days</div><div class="mv">${selected.workingDays}</div></div>
  <div><div class="ml">Days Present</div><div class="mv">${selected.presentDays}</div></div>
  <div><div class="ml">Absent Days</div><div class="mv">${selected.absentDays}</div></div>
  <div><div class="ml">Overtime Hours</div><div class="mv">${selected.overtimeHours || 0}</div></div>
  <div><div class="ml">Leave Taken</div><div class="mv">${selected.leaveDaysTaken || selected.leaveDays || 0} days</div></div>
  <div><div class="ml">Pay Period</div><div class="mv">${MONTHS[selected.month-1]} ${selected.year}</div></div>
</div>

<div class="ps-cols">
  <div class="ps-sec">
    <h4>Earnings</h4>
    ${[['Basic Salary',selected.basicSalary],['HRA',selected.hra],['Transport',selected.transport],['Medical',selected.medical],['Other Allowance',selected.otherAllowance],['Bonus',selected.bonus],['Overtime',selected.overtime]].filter(([,v])=>v>0).map(([l,v])=>`<div class="ps-row"><span>${l}</span><span>₹${(v||0).toLocaleString('en-IN')}</span></div>`).join('')}
    <div class="ps-total"><span>Gross Salary</span><span>₹${(selected.grossSalary||0).toLocaleString('en-IN')}</span></div>
  </div>
  <div class="ps-sec">
    <h4>Deductions</h4>
    ${[['Provident Fund (PF)',selected.pf],['ESI',selected.esi],['Income Tax',selected.tax],['Leave Deduction',selected.leaveDeduction],['Loan Deduction',selected.loanDeduction],['Other Deductions',selected.otherDeduction]].filter(([,v])=>v>0).map(([l,v])=>`<div class="ps-row red"><span>${l}</span><span>− ₹${(v||0).toLocaleString('en-IN')}</span></div>`).join('')}
    ${[selected.pf,selected.esi,selected.tax,selected.leaveDeduction].every(v=>!(v>0))?'<div style="color:#94a3b8;font-size:12px;padding:8px 0">No deductions</div>':''}
    <div class="ps-total"><span>Total Deductions</span><span style="color:#ef4444">₹${(selected.totalDeduction||0).toLocaleString('en-IN')}</span></div>
  </div>
</div>

<div class="ps-net">
  <div>
    <div style="font-size:12px;opacity:.7;margin-bottom:6px">NET SALARY (TAKE-HOME)</div>
    <div class="amount">₹${(selected.netSalary||0).toLocaleString('en-IN')}</div>
  </div>
  <div style="text-align:right">
    <div class="sub">Gross: ₹${(selected.grossSalary||0).toLocaleString('en-IN')}</div>
    <div class="sub" style="color:#fca5a5">Deductions: ₹${(selected.totalDeduction||0).toLocaleString('en-IN')}</div>
    ${selected.paidOn ? `<div class="sub" style="color:#86efac;margin-top:4px">Paid: ${new Date(selected.paidOn).toLocaleDateString('en-IN')}</div>` : ''}
  </div>
</div>

${(co.bankName||co.accountNo||co.ifscCode) ? `
<div class="bank-box">
  <h4>Payment Information</h4>
  <div class="bank-grid">
    ${co.bankName  ? `<div class="bank-item"><label>Bank Name</label><div>${co.bankName}</div></div>` : ''}
    ${co.accountNo ? `<div class="bank-item"><label>Account No.</label><div>${co.accountNo}</div></div>` : ''}
    ${co.ifscCode  ? `<div class="bank-item"><label>IFSC Code</label><div>${co.ifscCode}</div></div>` : ''}
  </div>
</div>` : ''}

<div class="footer">
  <span>Generated on ${new Date().toLocaleDateString('en-IN', {day:'2-digit',month:'long',year:'numeric'})}</span>
  <span>This is a computer-generated payslip. No signature required.</span>
</div>

</body></html>`;

    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 400);
  };

  const _oldHandlePrint = () => {
    const content = printRef.current?.innerHTML;
    if (!content) return;
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Payslip</title><style>
      * { box-sizing:border-box; margin:0; padding:0; }
      body { font-family:'Plus Jakarta Sans',sans-serif; padding:40px; color:#1e293b; background:#fff; }
      .ps-header { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:3px solid #1e293b; padding-bottom:20px; margin-bottom:24px; }
      .ps-company { font-size:22px; font-weight:900; color:#1e293b; }
      .ps-title { font-size:14px; color:#64748b; margin-top:4px; }
      .ps-badge { background:#1e293b; color:#fff; padding:6px 16px; border-radius:8px; font-size:12px; font-weight:700; }
      .ps-meta { display:grid; grid-template-columns:1fr 1fr; gap:16px; background:#f8fafc; border-radius:12px; padding:20px; margin-bottom:24px; }
      .ps-meta-item label { font-size:10px; text-transform:uppercase; letter-spacing:0.06em; color:#94a3b8; font-weight:700; }
      .ps-meta-item div { font-size:14px; font-weight:700; color:#1e293b; margin-top:2px; }
      .ps-cols { display:grid; grid-template-columns:1fr 1fr; gap:20px; margin-bottom:20px; }
      .ps-section h4 { font-size:11px; text-transform:uppercase; letter-spacing:0.06em; color:#94a3b8; font-weight:700; margin-bottom:12px; }
      .ps-row { display:flex; justify-content:space-between; padding:7px 0; border-bottom:1px solid #f1f5f9; font-size:13px; }
      .ps-row span:last-child { font-weight:600; }
      .ps-total { display:flex; justify-content:space-between; padding:10px 0; font-weight:800; font-size:14px; border-top:2px solid #1e293b; }
      .ps-net { background:#1e293b; color:#fff; border-radius:12px; padding:16px 24px; display:flex; justify-content:space-between; align-items:center; }
      .ps-net .label { font-size:13px; opacity:0.8; }
      .ps-net .amount { font-size:26px; font-weight:900; }
      .ps-footer { margin-top:32px; padding-top:16px; border-top:1px solid #e2e8f0; font-size:11px; color:#94a3b8; display:flex; justify-content:space-between; }
      @media print { body { padding:20px; } }
    </style></head><body>${content}</body></html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 400);
  };

  // ── Filtered payslips ────────────────────────────────────────────────────
  const filteredAll = allPayslips.filter(p => {
    if (filterMonth && p.month !== parseInt(filterMonth)) return false;
    if (filterYear  && p.year  !== parseInt(filterYear))  return false;
    if (filterStatus && p.status !== filterStatus)          return false;
    return true;
  });

  const totalGross = filteredAll.reduce((s,p)=>s+parseFloat(p.grossSalary||0),0);
  const totalNet   = filteredAll.reduce((s,p)=>s+parseFloat(p.netSalary||0),0);
  const totalDed   = filteredAll.reduce((s,p)=>s+parseFloat(p.totalDeduction||0),0);
  const paidCount  = filteredAll.filter(p=>p.status==='paid').length;

  // ── Styles ───────────────────────────────────────────────────────────────
  const S = {
    page: { maxWidth:1400, margin:'0 auto', fontFamily:"'Plus Jakarta Sans',sans-serif", paddingBottom:48 },
    card: { background:'#fff', borderRadius:20, padding:24, boxShadow:'0 2px 20px #00000009', border:'1px solid #f0f4f8' },
    tab:  (a) => ({ padding:'10px 20px', borderRadius:10, border:'none', fontWeight:700, fontSize:13, cursor:'pointer', transition:'all .2s',
      background: a ? '#1e293b' : '#f1f5f9', color: a ? '#fff' : '#64748b' }),
    btn:  (v) => ({ padding:'9px 18px', borderRadius:10, border:'none', fontWeight:700, fontSize:13, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:6, transition:'all .18s',
      ...(v==='primary' ? { background:'linear-gradient(135deg,#1e293b,#334155)', color:'#fff', boxShadow:'0 4px 14px #1e293b2a' }
        : v==='success' ? { background:'#dcfce7', color:'#14532d' }
        : v==='danger'  ? { background:'#fee2e2', color:'#dc2626' }
        :                 { background:'#f1f5f9', color:'#64748b' }) }),
    th: { padding:'11px 14px', textAlign:'left', fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.05em', borderBottom:'2px solid #f1f5f9', whiteSpace:'nowrap' },
    td: { padding:'13px 14px', fontSize:13, borderBottom:'1px solid #f8fafc', color:'#475569' },
  };

  if (loading && !isAdmin) return (
    <div style={{ textAlign:'center', padding:80 }}>
      <div style={{ width:40, height:40, border:'3px solid #f1f5f9', borderTop:'3px solid #1e293b', borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto' }}/>
    </div>
  );

  if (!isAdmin && !salaryVisible) return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'60vh', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <div style={{ fontSize:56, marginBottom:16 }}>🔒</div>
      <div style={{ fontSize:20, fontWeight:800, color:'#1e293b', marginBottom:8 }}>Salary Hidden</div>
      <div style={{ fontSize:14, color:'#64748b', textAlign:'center', maxWidth:360 }}>
        Your administrator has disabled salary visibility for employees.<br/>Contact your admin if you need to view your payslips.
      </div>
    </div>
  );

  return (
    <div style={S.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
        @keyframes spin { to { transform:rotate(360deg) } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(12px) } to { opacity:1; transform:translateY(0) } }
        .sal-row:hover { background:#f8fafc!important; }
        .sal-card:hover { border-color:#1e293b44!important; transform:translateY(-2px); box-shadow:0 8px 24px #0000000f!important; }
        input:focus, select:focus, textarea:focus { border-color:#1e293b!important; background:#fff!important; }
      `}</style>

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:28, flexWrap:'wrap', gap:16 }}>
        <div>
          <h1 style={{ margin:0, fontSize:32, fontWeight:900, color:'#0f172a', letterSpacing:'-0.02em' }}>💰 Salary & Payroll</h1>
          <p style={{ margin:'5px 0 0', color:'#94a3b8', fontSize:14 }}>{isAdmin ? `${employees.length} employees · ${allPayslips.length} payslips total` : `${payslips.length} payslips · Your salary history`}</p>
        </div>
      </div>

      {/* Tabs */}
      {isAdmin && (
        <div style={{ display:'flex', gap:6, marginBottom:24, background:'#f1f5f9', padding:4, borderRadius:12, width:'fit-content', flexWrap:'wrap' }}>
          {[['payslips','📄 My Payslips'],['all','👥 All Payrolls'],['generate','⚙️ Generate'],['structure','🏗 Structures']].map(([v,l])=>(
            <button key={v} style={S.tab(tab===v)} onClick={()=>setTab(v)}>{l}</button>
          ))}
        </div>
      )}

      {/* ── MY PAYSLIPS ─────────────────────────────────────────────────── */}
      {(tab==='payslips') && (
        <div style={{ display:'grid', gridTemplateColumns: selected ? '340px 1fr' : '1fr', gap:20, animation:'fadeUp .3s ease' }}>

          {/* Payslip cards */}
          <div>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {payslips.length === 0 && <div style={{ ...S.card, textAlign:'center', padding:48, color:'#94a3b8' }}>
                <div style={{ fontSize:40, marginBottom:12 }}>📭</div>
                <div style={{ fontWeight:700 }}>No payslips yet</div>
              </div>}
              {payslips.map(p => (
                <div key={p._id} className="sal-card" onClick={() => setSelected(p)}
                  style={{ ...S.card, cursor:'pointer', transition:'all .2s', borderLeft:`4px solid ${p.status==='paid'?'#22c55e':p.status==='approved'?'#3b82f6':'#f59e0b'}`,
                    background: selected?._id===p._id ? '#f0fdf4' : '#fff', borderColor: selected?._id===p._id ? '#22c55e' : '#f0f4f8' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                    <div>
                      <div style={{ fontWeight:800, fontSize:16, color:'#0f172a' }}>{MONTHS_S[p.month-1]} {p.year}</div>
                      <div style={{ fontSize:12, color:'#94a3b8', marginTop:2 }}>{p.workingDays} working days · {p.presentDays} present</div>
                    </div>
                    <span style={{ padding:'3px 10px', borderRadius:8, fontSize:11, fontWeight:800, background:p.status==='paid'?'#dcfce7':p.status==='approved'?'#dbeafe':'#fef3c7', color:p.status==='paid'?'#14532d':p.status==='approved'?'#1e40af':'#92400e' }}>
                      {p.status.toUpperCase()}
                    </span>
                  </div>
                  <div style={{ marginTop:14, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div>
                      <div style={{ fontSize:11, color:'#94a3b8', fontWeight:600 }}>NET SALARY</div>
                      <div style={{ fontSize:22, fontWeight:900, color:'#0f172a' }}>{INR(p.netSalary)}</div>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <div style={{ fontSize:11, color:'#94a3b8' }}>Gross {INR(p.grossSalary)}</div>
                      <div style={{ fontSize:11, color:'#ef4444' }}>Deductions {INR(p.totalDeduction)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Payslip detail */}
          {selected && (
            <div style={{ animation:'fadeUp .25s ease' }}>
              <div style={S.card}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:12 }}>
                  <h3 style={{ margin:0, fontWeight:800, fontSize:17 }}>Payslip — {MONTHS[selected.month-1]} {selected.year}</h3>
                  <div style={{ display:'flex', gap:8 }}>
                    <button style={S.btn()} onClick={() => setSelected(null)}>✕ Close</button>
                    <button style={S.btn('primary')} onClick={handlePrint}>🖨 Print / Download</button>
                    {isAdmin && selected.status !== 'paid' && (
                      <button style={S.btn('success')} onClick={() => markPaid(selected._id)}>✓ Mark Paid</button>
                    )}
                  </div>
                </div>

                {/* Printable payslip content */}
                <div ref={printRef}>
                  {/* Header */}
                  <div className="ps-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', borderBottom:'3px solid #1e293b', paddingBottom:20, marginBottom:24 }}>
                    <div>
                      <div className="ps-company" style={{ fontSize:22, fontWeight:900, color:'#1e293b' }}>Your Company</div>
                      <div className="ps-title" style={{ fontSize:13, color:'#64748b', marginTop:3 }}>Official Salary Statement</div>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <div className="ps-badge" style={{ background:'#1e293b', color:'#fff', padding:'6px 16px', borderRadius:8, fontSize:12, fontWeight:800, display:'inline-block' }}>PAYSLIP</div>
                      <div style={{ fontSize:13, color:'#64748b', marginTop:8 }}>{MONTHS[selected.month-1]} {selected.year}</div>
                      <div style={{ fontSize:12, color: selected.status==='paid'?'#22c55e':'#f59e0b', fontWeight:700 }}>{selected.status.toUpperCase()}</div>
                    </div>
                  </div>

                  {/* Employee info */}
                  <div className="ps-meta" style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:16, background:'#f8fafc', borderRadius:14, padding:20, marginBottom:24 }}>
                    {[
                      ['Employee', selected.employee?.name || user?.name || '—'],
                      ['Position', selected.employee?.position || user?.position || '—'],
                      ['Working Days', selected.workingDays],
                      ['Days Present', selected.presentDays],
                      ['Absent Days', selected.absentDays],
                      ['Overtime Hours', selected.overtimeHours || 0],
                      ['Leave Days Taken', selected.leaveDaysTaken || 0],
                      ['Pay Period', `${MONTHS[selected.month-1]} ${selected.year}`],
                    ].map(([l,v]) => (
                      <div key={l} className="ps-meta-item">
                        <label style={{ display:'block', fontSize:10, textTransform:'uppercase', letterSpacing:'0.06em', color:'#94a3b8', fontWeight:700 }}>{l}</label>
                        <div style={{ fontSize:14, fontWeight:700, color:'#1e293b', marginTop:2 }}>{v}</div>
                      </div>
                    ))}
                  </div>

                  {/* Earnings & Deductions */}
                  <div className="ps-cols" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:20 }}>
                    <div className="ps-section">
                      <h4 style={{ fontSize:11, textTransform:'uppercase', letterSpacing:'0.06em', color:'#94a3b8', fontWeight:700, marginBottom:12 }}>Earnings</h4>
                      {[
                        ['Basic Salary',      selected.basicSalary],
                        ['HRA',               selected.hra],
                        ['Transport',         selected.transport],
                        ['Medical',           selected.medical],
                        ['Other Allowance',   selected.otherAllowance],
                        selected.bonus     > 0 && ['Bonus',    selected.bonus],
                        selected.overtime  > 0 && ['Overtime', selected.overtime],
                      ].filter(Boolean).map(([l,v]) => (
                        v > 0 && <div key={l} className="ps-row" style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid #f1f5f9', fontSize:13 }}>
                          <span style={{ color:'#475569' }}>{l}</span><span style={{ fontWeight:600, color:'#0f172a' }}>{INR(v)}</span>
                        </div>
                      ))}
                      <div className="ps-total" style={{ display:'flex', justifyContent:'space-between', padding:'10px 0', fontWeight:800, fontSize:14, borderTop:'2px solid #1e293b', marginTop:4 }}>
                        <span>Gross Salary</span><span>{INR(selected.grossSalary)}</span>
                      </div>
                    </div>

                    <div className="ps-section">
                      <h4 style={{ fontSize:11, textTransform:'uppercase', letterSpacing:'0.06em', color:'#94a3b8', fontWeight:700, marginBottom:12 }}>Deductions</h4>
                      {[
                        selected.pf             > 0 && ['Provident Fund (PF)',   selected.pf],
                        selected.esi            > 0 && ['ESI',                   selected.esi],
                        selected.tax            > 0 && ['Income Tax',            selected.tax],
                        selected.leaveDeduction > 0 && [`Leave Deduction (${selected.leaveDays}d)`, selected.leaveDeduction],
                        selected.loanDeduction  > 0 && ['Loan Deduction',        selected.loanDeduction],
                        selected.otherDeduction > 0 && ['Other Deductions',      selected.otherDeduction],
                      ].filter(Boolean).map(([l,v]) => (
                        v > 0 && <div key={l} className="ps-row" style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid #f1f5f9', fontSize:13 }}>
                          <span style={{ color:'#475569' }}>{l}</span><span style={{ fontWeight:600, color:'#ef4444' }}>{INR(v)}</span>
                        </div>
                      ))}
                      {[['PF','ESI','Tax','Leave','Loan','Other'].every(k => !(selected[k.toLowerCase()] > 0)) && (
                        <div style={{ color:'#94a3b8', fontSize:13, padding:'8px 0' }}>No deductions</div>
                      )]}
                      <div className="ps-total" style={{ display:'flex', justifyContent:'space-between', padding:'10px 0', fontWeight:800, fontSize:14, borderTop:'2px solid #1e293b', marginTop:4 }}>
                        <span>Total Deductions</span><span style={{ color:'#ef4444' }}>{INR(selected.totalDeduction)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Net salary */}
                  <div className="ps-net" style={{ background:'#1e293b', color:'#fff', borderRadius:14, padding:'20px 24px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div>
                      <div className="label" style={{ fontSize:13, opacity:0.7, marginBottom:4 }}>NET SALARY (TAKE-HOME)</div>
                      <div className="amount" style={{ fontSize:32, fontWeight:900 }}>{INR(selected.netSalary)}</div>
                    </div>
                    <div style={{ textAlign:'right', opacity:0.8, fontSize:12 }}>
                      <div>Gross: {INR(selected.grossSalary)}</div>
                      <div style={{ color:'#fca5a5' }}>Deductions: {INR(selected.totalDeduction)}</div>
                      {selected.paidOn && <div style={{ color:'#86efac', marginTop:4 }}>Paid: {new Date(selected.paidOn).toLocaleDateString('en-IN')}</div>}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="ps-footer" style={{ marginTop:28, paddingTop:14, borderTop:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between', fontSize:11, color:'#94a3b8' }}>
                    <span>Generated on {new Date().toLocaleDateString('en-IN')}</span>
                    <span>This is a computer-generated payslip and does not require a signature.</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── ALL PAYROLLS ─────────────────────────────────────────────────── */}
      {isAdmin && tab === 'all' && (
        <div style={{ animation:'fadeUp .3s ease' }}>
          {/* Summary KPIs */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:14, marginBottom:20 }}>
            {[
              { l:'Total Payroll',   v: INR(totalGross),  c:'var(--pm-primary)', sub:`${filteredAll.length} employees` },
              { l:'Net Disbursed',   v: INR(totalNet),    c:'#22c55e', sub:'take-home total' },
              { l:'Total Deductions',v: INR(totalDed),    c:'#ef4444', sub:'PF + ESI + others' },
              { l:'Paid',            v: paidCount,        c:'#10b981', sub:`of ${filteredAll.length} payslips` },
              { l:'Pending',         v: filteredAll.length - paidCount, c:'#f59e0b', sub:'awaiting payment' },
            ].map(k => (
              <div key={k.l} style={{ ...S.card, borderLeft:`4px solid ${k.c}` }}>
                <div style={{ fontSize:11, color:'#94a3b8', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:6 }}>{k.l}</div>
                <div style={{ fontSize:24, fontWeight:900, color:'#0f172a' }}>{k.v}</div>
                <div style={{ fontSize:11, color:k.c, fontWeight:600, marginTop:4 }}>{k.sub}</div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div style={{ ...S.card, marginBottom:16 }}>
            <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'flex-end' }}>
              <div style={{ flex:'0 0 140px' }}>
                <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:5 }}>Month</label>
                <select style={INP} value={filterMonth} onChange={e=>setFilterMonth(e.target.value)}>
                  <option value="">All Months</option>
                  {MONTHS_S.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
                </select>
              </div>
              <div style={{ flex:'0 0 100px' }}>
                <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:5 }}>Year</label>
                <select style={INP} value={filterYear} onChange={e=>setFilterYear(e.target.value)}>
                  {[2024,2025,2026].map(y=><option key={y}>{y}</option>)}
                </select>
              </div>
              <div style={{ flex:'0 0 140px' }}>
                <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:5 }}>Status</label>
                <select style={INP} value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
                  <option value="">All Status</option>
                  <option value="draft">Draft</option>
                  <option value="approved">Approved</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
              <button style={{ ...S.btn(''), height:40 }} onClick={()=>{setFilterMonth('');setFilterYear(new Date().getFullYear());setFilterStatus('');}}>Clear</button>
            </div>
          </div>

          {/* Table */}
          <div style={S.card}>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                  <tr style={{ background:'#f8fafc' }}>
                    {['Employee','Period','Working Days','Present','Gross','Deductions','Net Salary','Status','Actions'].map(h=>(
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredAll.length === 0 && (
                    <tr><td colSpan={9} style={{ textAlign:'center', padding:48, color:'#94a3b8' }}>No payslips match filters</td></tr>
                  )}
                  {filteredAll.map((p,i) => (
                    <tr key={p._id} className="sal-row" style={{ background:i%2===0?'#fff':'#fafbfc', cursor:'pointer' }} onClick={()=>{setSelected(p);setTab('payslips');}}>
                      <td style={{ ...S.td, fontWeight:700, color:'#0f172a' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <div style={{ width:32, height:32, borderRadius:'50%', background:'linear-gradient(135deg,#1e293b,#475569)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:13, flexShrink:0 }}>
                            {(p.employee?.name||'?').charAt(0)}
                          </div>
                          <div>
                            <div>{p.employee?.name||'—'}</div>
                            <div style={{ fontSize:11, color:'#94a3b8', fontWeight:400 }}>{p.employee?.position||'—'}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ ...S.td, fontWeight:600 }}>{MONTHS_S[p.month-1]} {p.year}</td>
                      <td style={S.td}>{p.workingDays}</td>
                      <td style={S.td}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div style={{ width:50, height:5, background:'#f1f5f9', borderRadius:4, overflow:'hidden' }}>
                            <div style={{ height:'100%', width:`${p.workingDays?((p.presentDays/p.workingDays)*100):0}%`, background:'#22c55e', borderRadius:4 }}/>
                          </div>
                          <span>{p.presentDays}/{p.workingDays}</span>
                        </div>
                      </td>
                      <td style={{ ...S.td, fontWeight:600 }}>{INR(p.grossSalary)}</td>
                      <td style={{ ...S.td, color:'#ef4444' }}>{INR(p.totalDeduction)}</td>
                      <td style={{ ...S.td, fontWeight:800, color:'#0f172a', fontSize:14 }}>{INR(p.netSalary)}</td>
                      <td style={S.td}>
                        <span style={{ padding:'3px 10px', borderRadius:8, fontSize:11, fontWeight:800, background:p.status==='paid'?'#dcfce7':p.status==='approved'?'#dbeafe':'#fef3c7', color:p.status==='paid'?'#14532d':p.status==='approved'?'#1e40af':'#92400e' }}>
                          {p.status.toUpperCase()}
                        </span>
                      </td>
                      <td style={S.td} onClick={e=>e.stopPropagation()}>
                        <div style={{ display:'flex', gap:6 }}>
                          <button style={{ ...S.btn(), padding:'5px 10px', fontSize:11 }} onClick={()=>{setSelected(p);setTab('payslips');}}>View</button>
                          {p.status !== 'paid' && <button style={{ ...S.btn('success'), padding:'5px 10px', fontSize:11 }} onClick={()=>markPaid(p._id)}>Pay</button>}
                          <button style={{ ...S.btn('danger'), padding:'5px 10px', fontSize:11 }} onClick={()=>deletePayslip(p._id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── GENERATE PAYSLIPS ────────────────────────────────────────────── */}
      {isAdmin && tab === 'generate' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, animation:'fadeUp .3s ease' }}>
          <div style={S.card}>
            <h3 style={{ margin:'0 0 20px', fontSize:17, fontWeight:800 }}>⚙️ Generate Single Payslip</h3>
            <form onSubmit={generate} style={{ display:'flex', flexDirection:'column', gap:16 }}>
              <div>
                <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#475569', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Employee *</label>
                <select style={INP} required value={genForm.employeeId} onChange={e=>setGenForm(f=>({...f,employeeId:e.target.value}))}>
                  <option value="">Select employee</option>
                  {employees.map(e=><option key={e._id} value={e._id}>{e.name}{e.position?` — ${e.position}`:''}</option>)}
                </select>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#475569', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Month *</label>
                  <select style={INP} value={genForm.month} onChange={e=>setGenForm(f=>({...f,month:parseInt(e.target.value)}))}>
                    {MONTHS_S.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#475569', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Year *</label>
                  <input style={INP} type="number" value={genForm.year} onChange={e=>setGenForm(f=>({...f,year:parseInt(e.target.value)}))}/>
                </div>
              </div>
              <button type="submit" style={{ ...S.btn('primary'), justifyContent:'center', padding:'12px 0' }}>Generate Payslip</button>
            </form>
          </div>

          <div style={S.card}>
            <h3 style={{ margin:'0 0 8px', fontSize:17, fontWeight:800 }}>⚡ Bulk Generate</h3>
            <p style={{ margin:'0 0 20px', fontSize:13, color:'#94a3b8' }}>Generate payslips for <strong>all active employees</strong> at once. Employees without a salary structure will be skipped.</p>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
              <div>
                <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#475569', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Month</label>
                <select style={INP} value={bulkForm.month} onChange={e=>setBulkForm(f=>({...f,month:parseInt(e.target.value)}))}>
                  {MONTHS_S.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#475569', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Year</label>
                <input style={INP} type="number" value={bulkForm.year} onChange={e=>setBulkForm(f=>({...f,year:parseInt(e.target.value)}))}/>
              </div>
            </div>
            <div style={{ background:'#fef3c7', borderRadius:12, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#92400e' }}>
              ⚠️ This will generate payslips for {employees.length} employees for {MONTHS_S[bulkForm.month-1]} {bulkForm.year}. Already-generated payslips will be skipped.
            </div>
            <button style={{ ...S.btn('primary'), justifyContent:'center', padding:'12px 0', width:'100%' }} onClick={generateBulk}>
              ⚡ Generate for All {employees.length} Employees
            </button>
          </div>
        </div>
      )}

      {/* ── SALARY STRUCTURES ────────────────────────────────────────────── */}
      {isAdmin && tab === 'structure' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, animation:'fadeUp .3s ease' }}>
          <div style={S.card}>
            <h3 style={{ margin:'0 0 20px', fontSize:17, fontWeight:800 }}>🏗 Set Salary Structure</h3>
            <form onSubmit={saveStructure} style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div>
                <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#475569', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>Employee *</label>
                <select style={INP} required value={structForm.employeeId}
                  onChange={e=>{ setStructForm(f=>({...f,employeeId:e.target.value})); loadStructure(e.target.value); }}>
                  <option value="">Select employee</option>
                  {employees.map(e=><option key={e._id} value={e._id}>{e.name}{e.position?` — ${e.position}`:''}</option>)}
                </select>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                {[['basicSalary','Basic Salary','*'],['hra','HRA',''],['transport','Transport',''],['medical','Medical',''],['otherAllowance','Other Allowance','']].map(([key,label,req])=>(
                  <div key={key}>
                    <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#475569', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.04em' }}>{label}{req}</label>
                    <input style={INP} type="number" min="0" value={structForm[key]} onChange={e=>setStructForm(f=>({...f,[key]:parseFloat(e.target.value)||0}))}
                      placeholder="₹0" required={req==='*'}/>
                  </div>
                ))}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#475569', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.04em' }}>PF % (of basic)</label>
                  <input style={INP} type="number" step="0.1" min="0" max="100" value={structForm.pfPercent} onChange={e=>setStructForm(f=>({...f,pfPercent:parseFloat(e.target.value)}))}/>
                </div>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#475569', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.04em' }}>ESI %</label>
                  <input style={INP} type="number" step="0.01" min="0" max="100" value={structForm.esiPercent} onChange={e=>setStructForm(f=>({...f,esiPercent:parseFloat(e.target.value)}))}/>
                </div>
              </div>
              <button type="submit" style={{ ...S.btn('primary'), justifyContent:'center', padding:'12px 0' }}>Save Salary Structure</button>
            </form>
          </div>

          {/* Live preview */}
          {structForm.basicSalary > 0 && (
            <div style={S.card}>
              <h3 style={{ margin:'0 0 16px', fontSize:15, fontWeight:800, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.05em' }}>Live Preview</h3>
              {(() => {
                const gross = (parseFloat(structForm.basicSalary)||0)+(parseFloat(structForm.hra)||0)+(parseFloat(structForm.transport)||0)+(parseFloat(structForm.medical)||0)+(parseFloat(structForm.otherAllowance)||0);
                const pf    = ((parseFloat(structForm.basicSalary)||0) * (parseFloat(structForm.pfPercent)||0)) / 100;
                const esi   = (gross * (parseFloat(structForm.esiPercent)||0)) / 100;
                const net   = gross - pf - esi;
                return (
                  <>
                    {[['Basic Salary',structForm.basicSalary,'var(--pm-primary)'],['HRA',structForm.hra,'var(--pm-secondary)'],['Transport',structForm.transport,'#a78bfa'],['Medical',structForm.medical,'#c4b5fd'],['Other Allowance',structForm.otherAllowance,'#ddd6fe']].filter(([,v])=>v>0).map(([l,v,c])=>(
                      <div key={l} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'9px 0', borderBottom:'1px solid #f8fafc' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:'#475569' }}>
                          <div style={{ width:8, height:8, borderRadius:2, background:c }}/>
                          {l}
                        </div>
                        <span style={{ fontWeight:600 }}>{INR(v)}</span>
                      </div>
                    ))}
                    <div style={{ display:'flex', justifyContent:'space-between', padding:'10px 0', borderTop:'2px solid #1e293b', fontWeight:800, marginTop:4 }}>
                      <span>Gross Salary</span><span>{INR(gross)}</span>
                    </div>
                    <div style={{ marginTop:12, paddingTop:12, borderTop:'1px dashed #e2e8f0' }}>
                      {pf>0 && <div style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', fontSize:13 }}><span style={{ color:'#ef4444' }}>PF ({structForm.pfPercent}%)</span><span style={{ color:'#ef4444' }}>− {INR(pf)}</span></div>}
                      {esi>0 && <div style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', fontSize:13 }}><span style={{ color:'#ef4444' }}>ESI ({structForm.esiPercent}%)</span><span style={{ color:'#ef4444' }}>− {INR(esi)}</span></div>}
                    </div>
                    <div style={{ background:'#1e293b', color:'#fff', borderRadius:12, padding:'16px 20px', marginTop:12, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <div style={{ fontSize:12, opacity:0.7 }}>ESTIMATED NET</div>
                      <div style={{ fontSize:24, fontWeight:900 }}>{INR(net)}</div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
